import type { Branch, NearestBranchRequest } from "~/types/ecom/branch.type";
import type { Product } from "~/types/ecom/product/product.type";
import type {
  ProductStockRequest,
  ProductStockResponse,
} from "~/types/ecom/product/stock.type";
import type { Prettify } from "~/types/shared.type";
import {timeNowInSeconds} from "../utils/date";
import { SITEMAP_ENDPOINTS } from "~/assets/constants/ecomApi";
import type { CategorySiteMapStructure } from "~/types/ecom/sitemap.type";
import type { PreferredLanguageResponse } from "~/types/ecom/user.type";
import type { CategoriesResponse, SingleCategoryResponse } from "~/types/ecom/categories/categories.type";
import { useAjaxEcom } from "~/composables/useAjaxEcom";

let isProductListFetching = false;
// Token refresh request queue with a Promise-based semaphore pattern
let tokenRefreshPromise: Promise<any> | null = null;
const TOKEN_REFRESH_TIMEOUT = 10000; // 10 seconds timeout for token refresh

// TODO: move to customer service
export const CUSTOMER_ENDPOINTS = {
  GUEST_TROLLEYS: `/customers/guest/trolleys`,
  CUSTOMER_TROLLEYS: (customerId: string) =>
    `/customers/${customerId}/trolleys`,
};

export const LOCALITY_ENDPOINTS = {
  AUTOCOMPLETE: `/location-search/localities/autocomplete`,
};

export const NEWSLETTER_ENDPOINTS = {
  SIGNUP: `/newsletters/signup`,
};

export class EcomError extends Error {
  public constructor(message: string) {
    super();
    this.message = message;
  }
}

/* ECOM API SERVICE */
export class EcomService {
  public static accessTokenExpiry: number;
  public static newTokenGeneratedAt: number = 0;
  public static accessTokenPollInterval: number = 300;
  public static accessTokenExpiryBuffer: number = 900;

  /* Fetch all ts offline branches */
  async fetchAllBranches(nuxtApp = null,runtimeConfig = null) {
    // @ts-ignore
    try {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    const response = await useAjaxEcom<{ data: Branch[] }>("/branches", {
      show_notifications: false,
      show_loading_indicator: false,
    },nuxtApp,runtimeConfig);
    return response;
  } catch(err){
    const errorMeta = {branchesError: true}
    const { $customClientErrorHandler } = useNuxtApp();
    $customClientErrorHandler(
        err,
        'high',
        'EcomServiceError - trying to fetch all branches using fetchAllBranches()',
        errorMeta
    );  
    throw err;
  }
}

  /* Fetch a ts offline branch details based on its ID */
  async fetchBranchById(branchId: string, debug = false){
    try{
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    return await useAjaxEcom<{ data: Branch }>(`/branches/${branchId}`);
      }catch(err){
        const errorMeta = {branchId, branchDatalayerError: true}
        const { $customClientErrorHandler } = useNuxtApp();
        $customClientErrorHandler(
            err,
            'high',
            'EcomServiceError - trying to fetch branch by id using fetchBranchById()',
            errorMeta
        );  
        throw err;
      }
  }

  /* Fetch nearest branches based on postcode or latlang */
  async fetchNearestBranches(
    requestBody: NearestBranchRequest
  ): Promise<{ data: Branch[] }> {
    try{
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    // @ts-ignore
    return await useAjaxEcom("/branches/nearest", {
      method: "post",
      params: requestBody,
      show_notifications: false,
      show_loading_indicator: false,
    });}catch(err){
      const errorMeta = {nearestBranchError: true}
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
          err,
          'high',
          'EcomServiceError - trying to fetch nearest branches using fetchNearestBranches()',
          errorMeta
      );  
      throw err;
    }
  }

  /* Get products details from ECOM */
  static async fetchAllProducts(productRequest: {
    products: string[];
  }): Promise<{ data: Product[] }> {
    // Use a semaphore pattern instead of busy polling
    if (isProductListFetching) {
      // Create a promise that will resolve when the current fetch completes
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!isProductListFetching) {
            clearInterval(checkInterval);
            // Recursively call the function now that the previous request is done
            resolve(EcomService.fetchAllProducts(productRequest));
          }
        }, 200); // Check less frequently to reduce CPU load
      });
    }

    try {
      isProductListFetching = true;

      const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
      const response = await useAjaxEcom<{ data: Product[] }>("/products/getProducts", {
        method: "post",
        params: productRequest,
        show_loading_indicator: false,
      });

    isProductListFetching = false;
    return response;
    } catch (error) {
        isProductListFetching = false;

        const { $customClientErrorHandler } = useNuxtApp();
        const errorMeta = {
            ecomServiceError: true, productRequest,
        };

        $customClientErrorHandler(
            error,
            'high',
            'EcomServiceError: Error in fetchAllProducts() API call',
            errorMeta
        );

        throw error;
    }
}

  /* Get product stock details from ECOM >>>> site = branch */
  static async fetchStockProduct(
    stockRequest: Prettify<ProductStockRequest>,nuxtApp = null,runtimeConfig = null
  ): Promise<{ data: Prettify<ProductStockResponse>[] }> {
    try {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    // @ts-ignore
    return await useAjaxEcom("/stock/getStock", {
      method: "post",
      params: stockRequest,
      show_loading_indicator: false,
      show_notifications: false,
    },nuxtApp,runtimeConfig);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        const errorMeta = {
          ecomServiceError: true, stockRequest,
        };

        $customClientErrorHandler(
            error,
            'high',
            'EcomServiceError: Error in fetchStockProduct() API call',
            errorMeta
        );
        throw error; 
    }
}


  /* 
    // Directly get suggestions with place latitude, longitude from ECOM - woosmap is used by backend 
    static async fetchEcomLocalityDetails(
      input: string,
      woosmapPrivateApiKey: string,
      countries: string[] = ["nl"]
    ): Promise<any> {
      const components = countries
        .map((countryCode) => `country:${countryCode}`)
        .join("|");

      const localityQuery: any = {
        private_key: woosmapPrivateApiKey,
        input,
        types: "locality|postal_code",
        components,
      };

      const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
      return await useAjaxEcom(LOCALITY_ENDPOINTS.AUTOCOMPLETE, {
        query: localityQuery,
      });
    }
  */

  /* Newsletter subscribe method */
  static async subscribeToNewsletter(options: {
    email_address: string;
    ip?: string;
  }) {
    const requestBody: any = {
      email_address: options.email_address,
    };

    if (options.ip) requestBody["ip"] = options.ip;

    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    //@ts-ignore
    return await useAjaxEcom(NEWSLETTER_ENDPOINTS.SIGNUP, {
      method: "post",
      params: requestBody,
      show_loading_indicator: false,
    });
  }

  /* Refresh the ecom_access_token in cookies once expired*/
  static async getTopBrandsData() {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    const res = await useAjaxEcom("/content/pages/top-brand-menu");
    return res;
  }

  static async getAllBrandsData() {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    const res = await useAjaxEcom('/content/brands');
    return res;
  }

  static async postContactFormData(options: {
        email: string;
        name: string;
        feedback: string;
        feedback_type: string;
        order_number: string;
  }) {
    try{

    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    return await useAjaxEcom("/contact/feedback", {
      method: "post",
      params: options,
    });
  }
  catch(error){
    const { $customClientErrorHandler } = useNuxtApp();
    let errorMeta = {
      options,
      ecomServiceError: true,
    }
    $customClientErrorHandler(
        error,
        'high',
        'EcomServiceError - trying to post contact form with form Data using postContactFormData()',
        errorMeta
    );
    throw error;
  }
  }

  static async getCategoriesData(cache = false) {
    try {
      const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
      const res = await useAjaxEcom<CategoriesResponse>("/taxonomies?summary=true", {
        show_notifications: false,
        show_loading_indicator: false,
        cache
      });
      return res;
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        cache,
        menuStoreError: true,
      };

      $customClientErrorHandler(
        error,
        "high",
        "EcomServiceError - trying to get summarized categories using getCategoriesData()",
        errorMeta
      );
      throw error;
    }
  }
  

  static async getCategoryDetailsById(taxonomyId: string,nuxtApp = null,runtimeConfig = null) {
    try {
    const res = await useAjaxEcom<SingleCategoryResponse>(`/taxonomies/${taxonomyId}`,{
      show_notifications:false,
      show_loading_indicator:false,
      cache:true
    },nuxtApp,runtimeConfig);
    return res;
    } catch (error) {
      const nuxtApp = tryUseNuxtApp();
      if (nuxtApp) {
      const { $customClientErrorHandler } = nuxtApp;
      let errorMeta = {
        id: taxonomyId,
        menuStoreError: true,
      };
  
      $customClientErrorHandler(
        error,
        "high",
        "EcomServiceError - trying to get category using getCategoryDetailsById()",
        errorMeta
      );
    }
    }
  }

  /* Refresh the ecom_access_token in cookies once expired*/
  static async fetchToken(refresh = false) {
    try {
      let options = {
        query: {
          refresh
        }
      };

      const res = await useAjaxServer('/api/ecom/access-token', options);
      if (!res.access_token) {
        return {};
      }

      let val = {
        accessToken: res.access_token,
        expiresIn: res.expires_in,
        applicationName: res.application_name,
        tokenType: res.token_type,
        issuedAt: res.issued_at,
        generated: timeNowInSeconds() as number,
        expiresTime: (res.issued_at / 1000) + parseInt(res.expires_in, 10),
      };

      return val;
    } catch (error) {
      console.error("Token fetch error:", error);
      return error;
    }
  } 

  /* verify the ecom_access_token is valid */
  static isTokenValid(  accessToken: string | null,
                             expiresTime: number,
                             expiryBuffer: number) {

      if (!(accessToken && expiresTime)) {
        return true;
      }

      const now = timeNowInSeconds();
      return now < (expiresTime - expiryBuffer);
  }

  static async getPreferredLanguage(cutomerId: string) {
    try {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    return await useAjaxEcom<PreferredLanguageResponse>(`/customers/${cutomerId}/preferred-language`, {
      method: "get",
      headers: {
        'app-type': "Toolstation Web"
      }
    })
    } catch (error) {
      let errorMeta = {
        cutomerId,
        ecomServiceError: true
      }
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        'critical',
        'EcomServiceError - trying to fetch getPreferredLanguage using getPreferredLanguage()',
        errorMeta
      );
      throw error;
    }
  }

  static async setPreferredLanguage(cutomerId: string, data: PreferredLanguageResponse) {
    try {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    return await useAjaxEcom<PreferredLanguageResponse>(`/customers/${cutomerId}/preferred-language`, {
      method: "put",
      params: data,
      headers: {
        'app-type': "Toolstation Web"
      }
    })
    } catch (error) {
      let errorMeta = {
        cutomerId,
        data,
        ecomServiceError: true
      }
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        'critical',
        'EcomServiceError - trying to fetch setPreferredLanguage using setPreferredLanguage()',
        errorMeta
      );
      throw error;
    }
  }

  /**
   * Responds with the sitemap hierarchy for all categories
   */
  static async getCategorySitemap(nuxtApp = null,runtimeConfig = null): Promise<{ data: CategorySiteMapStructure[] }> {
    const { useAjaxEcom } = await import('~/composables/useAjaxEcom');
    return await useAjaxEcom(SITEMAP_ENDPOINTS.DEPARTMENTS,{
      show_notifications:false,
      show_loading_indicator:false
    },nuxtApp,runtimeConfig);
  }
}
